/**
 * Formats team name for Kubernetes label (teladoc/team annotation)
 * 
 * Kubernetes label values must:
 * - Be lowercase alphanumeric characters, hyphens, underscores, or dots
 * - Start and end with alphanumeric characters
 * - Be max 63 characters
 * 
 * This matches the format used in the deploy container:
 * TEAM_LABEL = "${TEAM.toLowerCase().replaceAll('^[0-9]', '').replaceAll('[^a-z0-9]', '-').replaceAll('-+', '-').replaceAll('(^-+|-+$)', '').trim().take(63)}"
 * 
 * @param teamName - The team name from Cortex (e.g., "Admin & Portals (Nucleus)", "Analytics Products (Marketing)")
 * @returns Formatted team name for Kubernetes label (e.g., "admin-portals-nucleus", "analytics-products-marketing")
 * 
 * @example
 * formatTeamNameForDeploy("Admin & Portals (Nucleus)") // "admin-portals-nucleus"
 * formatTeamNameForDeploy("Analytics Products (Clinical/PHA)") // "analytics-products-clinical-pha"
 * formatTeamNameForDeploy("AEM COE") // "aem-coe"
 */
export function formatTeamNameForDeploy(teamName: string): string {
  if (!teamName) return teamName;

  // Step 0: Strip [Deprecated] prefix (case-insensitive) so deprecated teams
  // produce the same label as their non-deprecated equivalent.
  const cleaned = teamName.replace(/^\[deprecated\]\s*/i, '');

  // Step 1: Convert to lowercase
  let formatted = cleaned.toLowerCase();
  
  // Step 2: Remove leading digits (Kubernetes labels can't start with numbers)
  formatted = formatted.replace(/^[0-9]+/, '');
  
  // Step 3: Replace all non-alphanumeric characters with hyphens
  // This includes spaces, parentheses, slashes, ampersands, etc.
  formatted = formatted.replace(/[^a-z0-9]/g, '-');
  
  // Step 4: Replace multiple consecutive hyphens with a single hyphen
  formatted = formatted.replace(/-+/g, '-');
  
  // Step 5: Remove leading and trailing hyphens
  formatted = formatted.replace(/^-+|-+$/g, '');
  
  // Step 6: Trim whitespace (shouldn't be any at this point, but safety check)
  formatted = formatted.trim();
  
  // Step 7: Limit to 63 characters (Kubernetes label value max length)
  formatted = formatted.substring(0, 63);
  
  // Step 8: Final cleanup - remove trailing hyphen if truncation created one
  formatted = formatted.replace(/-+$/, '');
  
  return formatted;
}

